import type { CSSProperties } from 'react'

import { cn } from '@/shared/lib/cn'

/**
 * Loading = skeletons con la FORMA REAL de la fila, nunca spinners que colapsan
 * el layout. La altura sale de `--row-h`, asi el esqueleto respeta la densidad
 * elegida y la lista no salta cuando llegan los datos.
 */

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div aria-hidden="true" style={style} className={cn('skeleton h-3 w-full', className)} />
}

export interface SkeletonRowsProps {
  rows?: number
  /** Ancho relativo de cada columna, en fracciones de la fila. */
  columns?: number[]
  withSpine?: boolean
  className?: string
}

export function SkeletonRows({
  rows = 8,
  columns = [0.06, 0.32, 0.16, 0.16, 0.14, 0.1],
  withSpine = true,
  className,
}: SkeletonRowsProps) {
  return (
    <div role="status" aria-label="Cargando" className={cn('flex flex-col', className)}>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="hairline-b flex items-center gap-3"
          style={{ height: 'var(--row-h)', paddingInline: 'var(--cell-px)' }}
        >
          {withSpine ? <div className="skeleton h-4 w-[var(--spine-w)] rounded-full" /> : null}
          {columns.map((width, columnIndex) => (
            <Skeleton key={columnIndex} className="h-3" style={{ width: `${width * 100}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function SkeletonCards({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div role="status" aria-label="Cargando" className={cn('grid gap-3 sm:grid-cols-2', className)}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="hairline flex flex-col gap-2 rounded-[var(--radius-card)] p-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonKpis({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div
      role="status"
      aria-label="Cargando"
      className={cn('grid grid-cols-2 gap-3 sm:grid-cols-4', className)}
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="hairline flex flex-col gap-2 rounded-[var(--radius-card)] p-3">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-6 w-1/3" />
        </div>
      ))}
    </div>
  )
}
